import React, { useEffect, useState, useCallback } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Navigate, useLocation } from "react-router-dom";
import {
  shouldProactivelyRefreshForActiveUser,
  shouldShowWarningForIdleUser,
  shouldTerminateSession,
  getWarningCountdownSeconds,
  cleanupExpiredToken,
  attemptTokenRefresh,
} from "../utils/tokenExpiration";
import { onSessionExpired } from "../utils/sessionExpiredEvent";
import SessionTimeoutWarningModal, {
  SessionModalState,
} from "./SessionTimeoutWarningModal";
import authStore from "../contexts/authStore";
import { onAuthBroadcast, broadcastAuthEvent } from "../utils/authBroadcast";
import { subscribeToActivity } from "../utils/userActivity";

interface ActivityAwareTokenGuardProps {
  children: React.ReactNode;
}

/**
 * Activity-aware token expiration guard that provides intelligent session management
 * - Active users: Get seamless token refresh without modal interruption
 * - Idle users: Get warning modal before session expires, then final logout
 */
const ActivityAwareTokenGuard: React.FC<ActivityAwareTokenGuardProps> = ({
  children,
}) => {
  const [isCheckingToken, setIsCheckingToken] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalState, setModalState] = useState<SessionModalState>("warning");
  const [warningCountdown, setWarningCountdown] = useState(60);
  const location = useLocation();

  // Start warning countdown timer
  const startWarningCountdown = useCallback(() => {
    const warningDuration = getWarningCountdownSeconds();
    setWarningCountdown(warningDuration);

    const interval = setInterval(() => {
      setWarningCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          // Time's up - switch to expired state
          setModalState("expired");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Stop warning countdown timer
  const stopWarningCountdown = useCallback(() => {
    setWarningCountdown(0);
  }, []);

  // Check token status and determine appropriate action
  const checkTokenStatus = useCallback(async () => {
    const token = authStore.getToken();

    if (!token) {
      cleanupExpiredToken();
      setIsCheckingToken(false);
      return;
    }

    // Check if session should be terminated (final logout)
    if (shouldTerminateSession(token)) {
      setModalState("expired");
      setShowModal(true);
      cleanupExpiredToken();
      broadcastAuthEvent("session-expired");
      setIsCheckingToken(false);
      return;
    }

    // Check if active user needs proactive token refresh
    if (shouldProactivelyRefreshForActiveUser(token)) {
      try {
        const refreshSuccess = await attemptTokenRefresh();
        if (refreshSuccess) {
          setIsCheckingToken(false);
          return;
        } else {
          // Fall through to warning/termination logic
        }
      } catch (error) {
        console.error("❌ Proactive token refresh error:", error);
        // Fall through to warning/termination logic
      }
    }

    // Check if idle user should see warning
    if (shouldShowWarningForIdleUser(token)) {
      setModalState("warning");
      setShowModal(true);
      startWarningCountdown();
      broadcastAuthEvent("session-warning");
      setIsCheckingToken(false);
      return;
    }

    // Token is valid and no action needed
    setIsCheckingToken(false);
  }, [startWarningCountdown]);

  // Re-check token on route changes
  useEffect(() => {
    setIsCheckingToken(true);
    checkTokenStatus();
  }, [location.pathname, checkTokenStatus]);

  // Set up periodic token checking (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      if (!showModal) {
        checkTokenStatus();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [checkTokenStatus, showModal]);

  // Listen for user activity changes - but don't auto-refresh when modal is shown
  useEffect(() => {
    const unsubscribe = subscribeToActivity((activityState) => {
      // console.log("👤 User activity changed:", activityState);
      // Only log activity changes, don't auto-refresh when modal is visible
      // The modal should only be dismissed by explicit user action (clicking buttons)
    });

    return unsubscribe;
  }, []);

  // Listen for global session expired events
  useEffect(() => {
    const cleanup = onSessionExpired(() => {
      // console.log("🎭 Session expired event received");
      setModalState("expired");
      setShowModal(true);
      cleanupExpiredToken();
      setIsCheckingToken(false);
    });
    return cleanup;
  }, []);

  // Listen for cross-tab authentication events
  useEffect(() => {
    const cleanup = onAuthBroadcast((event) => {
      // console.log(`🔄 Cross-tab auth event received: ${event.type}`, event);

      switch (event.type) {
        case "logged-in":
          setShowModal(false);
          stopWarningCountdown();
          setIsCheckingToken(false);

          // Prefer intendedDestination (e.g. user opened a protected deep link)
          const intendedDest = sessionStorage.getItem("intendedDestination");
          if (intendedDest) {
            sessionStorage.removeItem("intendedDestination");
            window.location.assign(intendedDest);
            break;
          }

          const lastPath =
            sessionStorage.getItem("lastVisitedPath") || "/domain";
          window.location.assign(lastPath);
          break;

        case "session-expired":
          setModalState("expired");
          setShowModal(true);
          cleanupExpiredToken();
          setIsCheckingToken(false);
          break;

        case "logged-out":
          setModalState("expired");
          setShowModal(true);
          cleanupExpiredToken();
          setIsCheckingToken(false);
          break;

        case "token-refreshed":
          // Token was refreshed on another tab
          const newToken = authStore.getToken();
          if (newToken) {
            setShowModal(false);
            stopWarningCountdown();
            setIsCheckingToken(false);
          }
          break;
      }
    });

    return cleanup;
  }, [stopWarningCountdown]);

  // Handle "Stay Logged In" button click
  const handleStayLoggedIn = async () => {
    // console.log("🔄 User clicked Stay Logged In");
    try {
      const refreshSuccess = await attemptTokenRefresh();
      if (refreshSuccess) {
        // console.log("✅ Token refresh successful");
        setShowModal(false);
        stopWarningCountdown();
      } else {
        // console.log("❌ Token refresh failed - switching to expired state");
        setModalState("expired");
      }
    } catch (error) {
      // console.error("❌ Token refresh error:", error);
      setModalState("expired");
    }
  };

  // Handle logout button click
  const handleLogout = () => {
    // console.log("🔄 User clicked Logout/Login Again");
    // Save the current page so the user can return after re-login
    const intendedDestination = `${location.pathname}${location.search || ""}`;
    sessionStorage.setItem("intendedDestination", intendedDestination);
    sessionStorage.removeItem("sessionExpiredModalShown");
    setShowModal(false);
    stopWarningCountdown();
    window.location.href = "/login";
  };

  // Show loading spinner while checking token
  if (isCheckingToken) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if no token
  const token = authStore.getToken();
  if (!token) {
    // Save the intended destination before redirecting to login
    // so the user can be redirected back after successful authentication.
    // Only save if the current path is a protected route (not login/signup/etc.)
    const currentPath = location.pathname;
    const isProtectedPath =
      currentPath !== "/login" &&
      currentPath !== "/signup" &&
      currentPath !== "/forgot-password" &&
      currentPath !== "/forgotpassword" &&
      currentPath !== "/403" &&
      currentPath !== "/";
    if (isProtectedPath) {
      const intendedDestination = `${currentPath}${location.search || ""}`;
      sessionStorage.setItem("intendedDestination", intendedDestination);
    }
    return <Navigate to="/login" replace />;
  }

  // Show session modal if needed
  if (showModal) {
    return (
      <SessionTimeoutWarningModal
        open={true}
        remainingSeconds={warningCountdown}
        modalState={modalState}
        onStayLoggedIn={
          modalState === "warning" ? handleStayLoggedIn : undefined
        }
        onLogout={handleLogout}
      />
    );
  }

  // Render protected content
  return <>{children}</>;
};

export default ActivityAwareTokenGuard;
